/**
 * Phira's default ("legacy") speed integrator, `useRpe170Speed` unset/false (W1-05a engine-eval; rpe-format.md
 * F§7.2, R§2.3, DESIGN.md §6.2 step 4, §6.11). Reimplemented from the research description; no Phira code.
 *
 *  1. Per layer with a speed list: keyframes (sec(start), start, LINEAR) and (sec(end), end, HOLD) in list order,
 *     every value pre-multiplied by SPEED_RATIO in f32. easingType, slices and bezier are IGNORED.
 *  2. S(t) = right-associative f32 sum of the layers (Anim rules: hold after an event, extrapolation before a
 *     layer's first event).
 *  3. pts = every keyframe time of every layer + max_time, sorted, deduplicated.
 *  4. For each neighbouring pair (a, b): s = S(a), e = S(b - 1e-4) (the 0.1 ms sample before the end); when
 *     signum(s)·signum(e) < 0 (Rust signum: +0 -> +1) the zero crossing a + (b - a)·s/(s - e) is added to pts.
 *  5. Sort and deduplicate again; for each pair a keyframe (a, height, tween) with the tween
 *     |s - e| < 1e-5 -> linear, |s| > |e| -> ClampedTween(OutQuad, 0..1 - e/s), else ClampedTween(InQuad, s/e..1)
 *     (the exact normalised integral of the linear ramp s -> e), then height += (s + e)·(b - a)/2 IN f64.
 *  6. The final keyframe (max_time, height, HOLD) is appended (after later keyframes when speed events extend
 *     past max_time; forward-play cursor semantics handle that ordering).
 * Stored heights are f32; the accumulation is f64.
 *
 * Internal API: `buildLegacyFloor(eventLayers, env, maxTime)`, `legacySpeedSum(eventLayers, env)` (S(t), for
 * readouts and tests).
 */

import { LINEAR_TWEEN } from '../../ease/tweens';
import type { IrLayer } from '../../ir/types';
import { F32 } from '../../math/f32';
import { AnimF32, type EvalEnv } from '../anim';
import { layerList, SPEED_RATIO_F32 } from '../channels';
import { floorCurve, FloorTrack, FloorTweenBuilder, zeroFloor, type FloorCurve } from './curve';

const fr = Math.fround;

/** The 0.1 ms the legacy integrator samples before each segment end. */
export const LEGACY_END_SAMPLE_SEC = 1e-4;

const LINEAR_DESC = { kind: 'static', tween: LINEAR_TWEEN } as const;

/** S(t): the summed legacy speed of a line (height units per second, f32), or null when no layer has speed. */
export interface LegacySpeedSum {
  readonly layers: readonly AnimF32[];
  at(t: number): number;
}

/** Per-layer linear speed tracks and their right-associative f32 sum (empty lists skipped). */
export function legacySpeedSum(eventLayers: readonly (IrLayer | null)[], env: EvalEnv): LegacySpeedSum {
  const layers: AnimF32[] = [];
  for (const layer of eventLayers) {
    const list = layerList(layer, 'speed');
    if (list === undefined || list.length === 0) continue;
    layers.push(AnimF32.fromEvents(list, env, { factor: SPEED_RATIO_F32, tweenOf: () => LINEAR_DESC }));
  }
  const scratch = new Float64Array(layers.length);
  return {
    layers,
    at(t: number): number {
      if (layers.length === 0) return 0;
      for (let i = 0; i < layers.length; i++) scratch[i] = layers[i]?.valueAt(t) ?? 0;
      return F32.sumChain(scratch, layers.length);
    },
  };
}

/** Rust `f32::signum`: +1 for +0 and positives, -1 for -0 and negatives, NaN for NaN. */
export function rustSignum(v: number): number {
  if (Number.isNaN(v)) return Number.NaN;
  return v < 0 || Object.is(v, -0) ? -1 : 1;
}

/** Sorted ascending, exact duplicates removed (NaN entries dropped). */
function sortDedup(xs: number[]): number[] {
  const sorted = xs.filter((x) => !Number.isNaN(x)).sort((a, b) => a - b);
  const out: number[] = [];
  for (const x of sorted) if (out.length === 0 || out[out.length - 1] !== x) out.push(x);
  return out;
}

/** The legacy floor curve of a line (H = 0 when no layer has a speed list). */
export function buildLegacyFloor(eventLayers: readonly (IrLayer | null)[], env: EvalEnv, maxTime: number): FloorCurve {
  const S = legacySpeedSum(eventLayers, env);
  if (S.layers.length === 0) return zeroFloor(maxTime);

  const raw: number[] = [maxTime];
  for (const a of S.layers) for (const t of a.timeline.times) raw.push(t);
  let pts = sortDedup(raw);

  const crossings: number[] = [];
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k] ?? 0;
    const b = pts[k + 1] ?? 0;
    const s = S.at(a);
    const e = S.at(b - LEGACY_END_SAMPLE_SEC);
    if (rustSignum(s) * rustSignum(e) < 0) crossings.push(a + (b - a) * fr(s / fr(s - e)));
  }
  if (crossings.length > 0) pts = sortDedup(pts.concat(crossings));

  const times: number[] = [];
  const values: number[] = [];
  const tweens = new FloorTweenBuilder();
  let height = 0;
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k] ?? 0;
    const b = pts[k + 1] ?? 0;
    const s = S.at(a);
    const e = S.at(b - LEGACY_END_SAMPLE_SEC);
    times.push(a);
    values.push(fr(height));
    tweens.ramp(s, e);
    height += (fr(s + e) * (b - a)) / 2;
  }
  times.push(maxTime);
  values.push(fr(height));
  tweens.hold();
  return floorCurve('legacy', maxTime, [new FloorTrack(times, values, tweens.build())]);
}
